import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { linksMenu, navLinks, siteInfo } from "@/mocks/home";

const LOGO =
  "https://images.squarespace-cdn.com/content/v1/55db92c4e4b035628e290c2f/4233ec3a-bfc5-4a6b-82d6-b13b2f98246d/hgcseal22.jpeg?format=300w";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [linksOpen, setLinksOpen] = useState(false);
  const [membersOpen, setMembersOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 80);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const solid = scrolled || mobileOpen;

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-colors duration-500 ${
        solid
          ? "bg-background-50/95 backdrop-blur-md border-b border-background-200/70"
          : "bg-transparent"
      }`}
    >
      <div className="w-full px-4 md:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <a href="#home" className="flex items-center gap-3 group cursor-pointer">
            <span className="w-12 h-12 shrink-0 overflow-hidden rounded-full border border-background-300/60">
              <img
                src={LOGO}
                alt={`${siteInfo.name} seal`}
                title={`${siteInfo.name} seal`}
                className="w-full h-full object-cover"
              />
            </span>
            <span className="hidden sm:flex flex-col leading-none">
              <span
                className={`font-heading text-lg md:text-xl tracking-wide transition-colors duration-500 ${
                  solid ? "text-foreground-950" : "text-white"
                }`}
              >
                Hillsborough Garden Club
              </span>
              <span
                className={`mt-1 text-[11px] uppercase tracking-[0.28em] transition-colors duration-500 ${
                  solid ? "text-foreground-600" : "text-white/80"
                }`}
              >
                {siteInfo.established}
              </span>
            </span>
          </a>

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-8">
            <a
              href="#home"
              className={`text-sm tracking-wide transition-colors duration-300 ${
                solid ? "text-foreground-700 hover:text-primary-600" : "text-white/90 hover:text-white"
              }`}
            >
              Home
            </a>

            {/* Links dropdown */}
            <div
              className="relative"
              onMouseEnter={() => setLinksOpen(true)}
              onMouseLeave={() => setLinksOpen(false)}
            >
              <button
                type="button"
                className={`flex items-center gap-1 text-sm tracking-wide whitespace-nowrap transition-colors duration-300 ${
                  solid ? "text-foreground-700 hover:text-primary-600" : "text-white/90 hover:text-white"
                }`}
                aria-haspopup="true"
                aria-expanded={linksOpen}
              >
                Links
                <i className="ri-arrow-down-s-line text-base" aria-hidden="true" />
              </button>
              <div
                className={`absolute left-1/2 -translate-x-1/2 top-full pt-3 transition-all duration-200 ${
                  linksOpen ? "opacity-100 visible translate-y-0" : "opacity-0 invisible -translate-y-1"
                }`}
              >
                <div className="min-w-[220px] rounded-lg border border-background-200/80 bg-background-50 py-2 shadow-sm">
                  {linksMenu.map((item) => (
                    <a
                      key={item.label}
                      href={item.href}
                      className="block px-4 py-2 text-sm text-foreground-700 hover:bg-background-100 hover:text-primary-600 transition-colors"
                    >
                      {item.label}
                    </a>
                  ))}
                </div>
              </div>
            </div>

            <a
              href="#things-we-like"
              className={`text-sm tracking-wide transition-colors duration-300 ${
                solid ? "text-foreground-700 hover:text-primary-600" : "text-white/90 hover:text-white"
              }`}
            >
              Flower Show Education
            </a>

            {/* Members dropdown */}
            <div
              className="relative"
              onMouseEnter={() => setMembersOpen(true)}
              onMouseLeave={() => setMembersOpen(false)}
            >
              <button
                type="button"
                className={`flex items-center gap-1 text-sm tracking-wide whitespace-nowrap transition-colors duration-300 ${
                  solid ? "text-foreground-700 hover:text-primary-600" : "text-white/90 hover:text-white"
                }`}
                aria-haspopup="true"
                aria-expanded={membersOpen}
              >
                Members
                <i className="ri-arrow-down-s-line text-base" aria-hidden="true" />
              </button>
              <div
                className={`absolute left-1/2 -translate-x-1/2 top-full pt-3 transition-all duration-200 ${
                  membersOpen ? "opacity-100 visible translate-y-0" : "opacity-0 invisible -translate-y-1"
                }`}
              >
                <div className="min-w-[160px] rounded-lg border border-background-200/80 bg-background-50 py-2 shadow-sm">
                  <Link
                    to="/members"
                    className="block px-4 py-2 text-sm text-foreground-700 hover:bg-background-100 hover:text-primary-600 transition-colors"
                  >
                    Sign In
                  </Link>
                </div>
              </div>
            </div>
          </nav>

          {/* Mobile toggle */}
          <button
            type="button"
            onClick={() => setMobileOpen((v) => !v)}
            aria-label="Toggle menu"
            className={`lg:hidden w-10 h-10 flex items-center justify-center cursor-pointer transition-colors duration-500 ${
              solid ? "text-foreground-900" : "text-white"
            }`}
          >
            <i className={mobileOpen ? "ri-close-line text-2xl" : "ri-menu-line text-2xl"} aria-hidden="true" />
          </button>
        </div>
      </div>

      {/* Mobile panel */}
      <div
        className={`lg:hidden overflow-hidden transition-all duration-300 ${
          mobileOpen ? "max-h-[520px] border-t border-background-200/70" : "max-h-0"
        } bg-background-50`}
      >
        <nav className="px-4 py-4 flex flex-col">
          {navLinks.map((item) => (
            <a
              key={item.id}
              href={item.href}
              onClick={() => setMobileOpen(false)}
              className="py-3 text-sm text-foreground-800 border-b border-background-200/60 hover:text-primary-600 transition-colors"
            >
              {item.label}
            </a>
          ))}
          <div className="pt-3 text-[11px] uppercase tracking-[0.28em] text-foreground-500">Links</div>
          {linksMenu.map((item) => (
            <a
              key={item.label}
              href={item.href}
              onClick={() => setMobileOpen(false)}
              className="py-2.5 pl-3 text-sm text-foreground-700 hover:text-primary-600 transition-colors"
            >
              {item.label}
            </a>
          ))}
          <Link
            to="/members"
            onClick={() => setMobileOpen(false)}
            className="mt-2 inline-flex items-center gap-2 text-sm text-primary-600"
          >
            Member Sign In
            <i className="ri-arrow-right-line" aria-hidden="true" />
          </Link>
        </nav>
      </div>
    </header>
  );
}